import type { PayloadInterface } from '~/types/AppData/AppDataInterface';
import type { ArticleInterface } from '~/types/AppData/PageContent/ArticleInterface';

/**
 * Generates client API data by extracting relevant information from the provided payload and article data.
 *
 * @param {PayloadInterface} data - The payload data containing property settings, experiments,
 *                                  theme palette, and viewer information.
 * @param {ArticleInterface} article - The article data including metadata, feeds, tags, and custom properties.
 * @returns {object} An object containing `pageData` and `paletteData` with the extracted information.
 *
 * The returned `pageData` includes:
 * - `tags`: Commercial tags from the article metadata.
 * - `distributionChannels`: Feeds from the article data.
 * - `language`: Language setting from the payload property settings.
 * - `experiments`: Experiments information from the payload data.
 * - `pageType`: Page type from the payload data.
 * - `articleId`: Article ID from the article data.
 * - `property`: Property setting from the payload property settings.
 * - `contentTags`: Tags from the article data.
 * - `isMobileViewer`: Viewer type from the payload data indicating if the viewer is mobile.
 * - `topic`: Custom topic from the article data.
 * - `vertical`: Custom vertical from the article data.
 *
 * The returned `paletteData` includes:
 * - `colors`: Colors from the payload theme palette.
 * - `fonts`: Fonts from the payload theme palette.
 * - `typography`: Typography from the payload theme palette.
 */
export const getClientApiData = (
  data: PayloadInterface,
  article?: ArticleInterface
): object => {
  return {
    pageData: {
      tags: article?.metadata?.commercialTags,
      distributionChannels: article?.feeds,
      language: data.propertySettings.language,
      experiments: data.experiments,
      pageType: data.type,
      articleId: article?.id,
      property: data.propertySettings.property,
      contentTags: article?.tags,
      isMobileViewer: data.isMobileViewer,
      topic: article?.customTopic,
      vertical: article?.customVertical,
      siteName: data.propertySettings.siteName,
    },
    paletteData: {
      colors: data.theme.palette.colors,
      fonts: data.theme.palette.fonts,
      typography: data.theme.palette.typography,
    },
  };
};
